/**
 * ground-truth-export-flow.ts — the in-editor "Export as ground truth" action.
 *
 * A THIN sibling of the "Download edited PDF" action
 * (`editor-modals-save.ts:downloadEditedFile`): identical browser-download
 * plumbing (`download-util.ts:triggerBlobDownload`), differing ONLY in the
 * payload — it serializes the in-memory corrected IR to a `GroundTruthDoc` JSON
 * oracle artifact via the LANDED `exportGroundTruth` (Phase 2) instead of the
 * remediated PDF. No server endpoint, no new pipeline (design G-5: in-editor
 * export is the primary surface, the author is IN the editor with the finished
 * hand-authored structure).
 *
 * Epic: docs/epics/active/editor-ground-truth-authoring-260813/main.md Phase 3.
 *
 * DOM-free at module scope: it imports NO `editor-state` singleton, so the pure
 * core (pageCanvas derivation, filename stamping, serialize) is unit-testable
 * without the ~55-element editor DOM scaffold. All DOM/state coupling
 * (`state.ir`, the toolbar button, `triggerBlobDownload`, `announce`) is INJECTED
 * by the composition root (`editor.ts`) via `GroundTruthExportDeps`.
 *
 * Coordinate-space note (the Phase-2 [FIX] LANDED in Phase 3 — see phase-3 doc
 * §Findings): the runtime IR `Block.bbox` is PDF user-space (origin BOTTOM-left) —
 * see `EditorIRBBox` (backend) + `canvasBboxToPdf`/`set-bbox`. The per-page MediaBox
 * dims supplied below (`pageCanvasFromIr`/`composePageCanvas`, PDF user-space points)
 * are the reference `exportGroundTruth` normalizes against; its hand-authored leg
 * reconciles to the canonical top-left `BboxNorm` scoring space via
 * `pdfUserSpaceXywhToNorm` — WITH the origin flip `top = pageHeight - (y + h)` — so
 * every geometry leg (x/w/h AND the flipped y) is now geometrically sound. The
 * structure legs (role / reading order / text / provenance) are correct too.
 *
 * @module editor/ground-truth-export-flow
 */
import { log } from "./log.js";
import { exportGroundTruth, } from "./ground-truth-doc.js";
// ─── Pure core (DOM-free, unit-testable) ────────────────────────────────────
/**
 * Per-page canvas-dimension lookup for `exportGroundTruth`, derived from the IR
 * page records. Each `Page` carries its `/MediaBox` `width`/`height` in PDF
 * user-space points (see the wire contract), which is the reference the C# side
 * exports "so offline consumers can normalise per-block bboxes." Keyed by the
 * 1-based `page.number`, matching `Block.page`.
 */
export function pageCanvasFromIr(ir) {
    const byNumber = new Map();
    for (const p of ir.pages ?? [])
        byNumber.set(p.number, { w: p.width, h: p.height });
    return (page) => byNumber.get(page) ?? null;
}
/**
 * Compose the effective per-page dims lookup: try `primary` first (the
 * composition root supplies the RENDERED page's natural PDF-point dims, sourced
 * from the page canvas — see editor.ts), then fall back to the wire IR's
 * `pages[]` MediaBox. WHY the primary source is load-bearing (bug #1, 260813):
 * the backend `editor-ir` load path (`normalizeIRPayload`) does NOT populate
 * `ir.pages`, so `pageCanvasFromIr` alone returns null for every page and the
 * export throws "no page-canvas dims" — blocking ground-truth authoring on every
 * real document. The rendered-page dims ARE available (set on the canvas at
 * render time, same PDF-point space as `Block.bbox`), so they are the reliable
 * producer; `ir.pages` remains a fallback for documents that carry it.
 */
export function composePageCanvas(primary, ir) {
    const fallback = pageCanvasFromIr(ir);
    if (!primary)
        return fallback;
    return (page) => {
        const p = primary(page);
        if (p && p.w > 0 && p.h > 0)
            return p;
        return fallback(page);
    };
}
/** Two-digit zero-pad. */
function pad2(n) {
    return String(n).padStart(2, "0");
}
/**
 * Stamped ground-truth filename: `<base>-ground-truth-YYMMDD.HHMMSS.json`
 * (rule #6 — a timestamp so successive exports don't silently overwrite). The
 * base is the source doc name with any trailing extension stripped.
 */
export function stampedGroundTruthFilename(sourceName, now) {
    const base = (sourceName ?? "document").replace(/\.[A-Za-z0-9]+$/, "") || "document";
    const stamp = pad2(now.getFullYear() % 100) + pad2(now.getMonth() + 1) + pad2(now.getDate())
        + "." + pad2(now.getHours()) + pad2(now.getMinutes()) + pad2(now.getSeconds());
    return `${base}-ground-truth-${stamp}.json`;
}
/**
 * Serialize the hand-authored IR to a `GroundTruthDoc` (the payload). Thin wrapper
 * over the Phase-2 `exportGroundTruth` that supplies the MediaBox page-canvas
 * lookup. Fails loud (throws `GroundTruthExportError`) when an element lacks
 * geometry or a page has no dims — the caller converts that into a user-facing
 * toast + announcement rather than serializing an unscoreable box.
 */
export function serializeGroundTruth(ir, provenance, pageCanvas) {
    return exportGroundTruth(ir, provenance, composePageCanvas(pageCanvas, ir));
}
/** Hex-encode a SHA-256 digest of `bytes` via WebCrypto. */
export async function sha256Hex(bytes) {
    // Copy into a fresh ArrayBuffer-backed view so digest() accepts it under both
    // tsc gates (mirrors the downloadEditedFile Blob-part construction).
    const digest = await crypto.subtle.digest("SHA-256", new Uint8Array(bytes));
    return Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, "0")).join("");
}
/**
 * Run one export: read the IR, build provenance, serialize the `GroundTruthDoc`,
 * and download it as stamped JSON. Returns the produced doc (or null on a
 * no-document / failure path) so tests can assert on the payload.
 */
export async function runGroundTruthExport(deps) {
    const ir = deps.getIr();
    if (!ir) {
        deps.announce("No document loaded to export as ground truth.");
        return null;
    }
    // Provenance SHA is best-effort: a missing hash weakens provenance but must
    // not block the export (the structure legs are the load-bearing payload).
    let sha = "";
    try {
        sha = await deps.computeSha();
    }
    catch (err) {
        log.warn("[editor] ground-truth export: source SHA-256 unavailable:", err);
    }
    const provenance = {
        sourceDocSha256: sha,
        sourceDocName: deps.getSourceName() ?? "document.pdf",
        authoredBy: "editor",
        editorVersion: deps.getBuildVersion(),
        format: "pdf",
        pageCount: ir.pages?.length ?? null,
    };
    let gt;
    try {
        gt = serializeGroundTruth(ir, provenance, deps.pageCanvas);
    }
    catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        deps.announce(`Ground-truth export failed: ${msg}`);
        if (deps.onError)
            deps.onError(`Ground-truth export failed: ${msg}`);
        log.error("[editor] ground-truth export failed:", err);
        return null;
    }
    const filename = stampedGroundTruthFilename(deps.getSourceName(), (deps.now ?? (() => new Date()))());
    const blob = new Blob([JSON.stringify(gt, null, 2)], { type: "application/json" });
    deps.download(blob, filename);
    deps.announce(`Exported ground truth: ${filename}`);
    return gt;
}
/**
 * Wire the "Export as ground truth" toolbar button to `runGroundTruthExport`.
 * Idempotent-safe to call once at boot; a null button (partial DOM) no-ops.
 */
export function installGroundTruthExport(deps) {
    if (!deps.button)
        return;
    deps.button.addEventListener("click", () => {
        void runGroundTruthExport(deps);
    });
}
// ─── Non-prod visibility gate ───────────────────────────────────────────────
/**
 * True when the env label denotes a NON-prod environment (staging / local dev).
 * Mirrors `env-banner.ts`'s `/^prod/i` predicate (A.19 — one env-classification
 * rule across the editor page). `null`/empty is treated as prod (the safe default).
 */
export function isNonProdEnvLabel(label) {
    return !!label && !/^prod/i.test(label);
}
/**
 * Env-gate the "Export as ground truth" button. It is a corpus-authoring tool for
 * us — shown in NON-prod (staging / local dev) and **removed entirely** in prod (it
 * is noise for customers). REMOVE, not display:none, so it is genuinely absent from
 * the prod DOM. Prod-safe default: an unknown/absent label removes the button, so a
 * failed env fetch never leaks the button into prod.
 *
 * Sync + DOM-only (the env label is supplied by the caller, who reuses the existing
 * `/api/auth/me` signal — the same one `env-banner.ts` consumes), so this gate is
 * deterministically testable without mocking fetch.
 */
export function applyExportButtonEnvGate(button, envLabel) {
    if (!button)
        return;
    if (!isNonProdEnvLabel(envLabel))
        button.remove();
}
